import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { Popover, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { StoreBizHour } from 'models'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BizHourDisplay } from './BizHourDisplay'
import { convertToWeekday, currentTzTime } from 'services'
import { useCurrentStore } from 'hooks'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface BizHoursSectionProps {
  isOpening: boolean
  currentBizHour?: StoreBizHour
}

export const BizHoursSection: FC<BizHoursSectionProps> = ({
  isOpening,
  currentBizHour,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { store } = useCurrentStore()

  // Handle popover
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const id = open ? 'biz-hour-popover' : undefined

  // Update biz hour order
  const updatedOrderBizHours = useMemo(() => {
    if (store.biz_hours) {
      const today = currentTzTime(store.timezone)
      const currentIndex = store.biz_hours.findIndex(
        (b) => b.day_of_week === today.weekday(),
      )
      let movedBizHours = store.biz_hours.slice(0, currentIndex)

      let remainingBizHours = store.biz_hours.slice(
        currentIndex,
        store.biz_hours.length,
      )

      // Destructuring to create the desired array
      return [...remainingBizHours, ...movedBizHours]
    }

    return undefined
  }, [store.biz_hours]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="start"
        onClick={handleClick}
        sx={{
          width: 'max-content',
          ':hover': {
            cursor: 'pointer',
            backgroundColor: theme.palette.grey['A100'],
          },
        }}
      >
        <AccessTimeIcon color="primary" />
        <Stack spacing={2}>
          {!isOpening && (!currentBizHour || !currentBizHour.is_working) && (
            <Typography variant="body2">
              {t('Store is closed today')}
            </Typography>
          )}
          {isOpening && !currentBizHour && (
            <Typography variant="body2">
              {t('Store is opened all day')}
            </Typography>
          )}
          {currentBizHour && currentBizHour.is_working && (
            <BizHourDisplay bizHour={currentBizHour} />
          )}
        </Stack>
        <ExpandMoreIcon color="primary" />
      </Stack>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="start"
          sx={{ padding: 2 }}
        >
          <AccessTimeIcon color="primary" />
          <Stack spacing={2}>
            {!updatedOrderBizHours ? (
              <Typography variant="body2">
                {t('Store is always opened')}
              </Typography>
            ) : (
              updatedOrderBizHours.map((bizHour) => (
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" width="30%">
                    {convertToWeekday(bizHour.day_of_week!)}
                  </Typography>
                  {!bizHour.is_working ? (
                    <Typography variant="body2">{t('Closed')}</Typography>
                  ) : (
                    <BizHourDisplay bizHour={bizHour} />
                  )}
                </Stack>
              ))
            )}
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
